import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { visuallyHidden } from '@mui/utils';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import ItemForm from './Form';
import Show from './Show';
import DeleteConfirmation from '../../components/DeleteConfirmation';
import SalePointTypeService from '../../../services/salePointType.service';
import { useState, useEffect } from 'react';

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
  {
    id: 'name',
    numeric: false,
    label: 'Nom',
  },
  {
    id: 'actions',
    numeric: false,
    label: '',
  },
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } =
    props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead sx={{ backgroundColor: '#f8f9fa' }}>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding='normal'
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

function EnhancedTableToolbar({ searchQuery, onSearchChange, onAddClick }) {
  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        display: 'flex',
        justifyContent: 'space-between',
        borderBottom: '1px solid #f0f0f0'
      }}
    >
      <Typography
        variant="h6"
        component="div"
        sx={{ 
          fontSize: '20px',
          fontWeight: 'bold'
        }}
      >
        Types de points de vente
      </Typography>
      <div className="d-flex align-items-center gap-3">
        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            size='small'
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Rechercher..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" >
                  <SearchIcon sx={{ color: '#666' }} fontSize='small' />
                </InputAdornment>
              ),
            }}
            sx={{
              backgroundColor: 'white',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                '& fieldset': {
                  borderColor: '#e0e0e0',
                },
              }
            }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ 
            backgroundColor: 'primary.main',
            borderRadius: '8px',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#333'
            }
          }}
          onClick={onAddClick}
        >
          Ajouter
        </Button>
      </div>
    </Toolbar>
  );
}

EnhancedTableToolbar.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onAddClick: PropTypes.func.isRequired,
};

function ActionMenu({ row, onEditClick, onShowClick, onDeleteClick }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const open = Boolean(anchorEl);
  
  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = (event) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  const handleEdit = (event) => {
    handleClose(event);
    onEditClick(row);
  };

  const handleShow = (event) => {
    handleClose(event);
    onShowClick(row);
  };

  const handleDeleteClick = (event) => {
    handleClose(event);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    onDeleteClick(row);
    setDeleteConfirmOpen(false);
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={handleShow} sx={{ color: 'primary.main', gap: 1}}> 
          <InfoIcon /> <span style={{ marginTop: '4px' }}>Détails</span>
        </MenuItem>
        <MenuItem onClick={handleEdit} sx={{ color: 'orange', gap: 1}}>
          <EditIcon /> <span style={{ marginTop: '4px' }}>Modifier</span>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'red', gap: 1}}>
          <DeleteIcon /> <span style={{ marginTop: '4px' }}>Supprimer</span>
        </MenuItem>
      </Menu>

      <DeleteConfirmation
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le type de point de vente"
        message={`Êtes-vous sûr de vouloir supprimer le type "${row.name}" ?`}
      />
    </>
  );
}

export default function List() {
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('name');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [isFormOpen, setIsFormOpen] = React.useState(false);
  const [isShowOpen, setIsShowOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [salePointTypes, setSalePointTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchSalePointTypes();
  }, []);

  const fetchSalePointTypes = async () => {
    try {
      setLoading(true);
      const data = await SalePointTypeService.list();
      setSalePointTypes(data);
    } catch (err) {
      setError(err.message);
      setSnackbar({
        open: true,
        message: 'Erreur lors du chargement des types de points de vente',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = salePointTypes.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleShowClose = () => {
    setIsShowOpen(false);
    setSelectedItem(null);
  };

  const handleAddClick = () => {
    setSelectedItem(null);
    setIsFormOpen(true);
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setIsFormOpen(true);
  };

  const handleShowClick = (item) => {
    setSelectedItem(item);
    setIsShowOpen(true);
  };

  const handleDeleteClick = async (item) => {
    try {
      await SalePointTypeService.delete(item.id);
      await fetchSalePointTypes();
      setSnackbar({
        open: true,
        message: 'Type de point de vente supprimé avec succès',
        severity: 'success'
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression',
        severity: 'error'
      });
    }
  };

  const handleFormClose = async (formData, onError) => {
    if (formData) {
      try {
        if (selectedItem) {
          await SalePointTypeService.update({...formData, id: selectedItem.id});
          setSnackbar({
            open: true,
            message: 'Type de point de vente modifié avec succès',
            severity: 'success'
          });
          setIsFormOpen(false);
          setSelectedItem(null);
          await fetchSalePointTypes();
        } else {
          await SalePointTypeService.create(formData);
          setSnackbar({
            open: true,
            message: 'Type de point de vente ajouté avec succès',
            severity: 'success'
          });
          setIsFormOpen(false);
          setSelectedItem(null);
          await fetchSalePointTypes();
        }
      } catch (err) {
        // Si c'est une erreur de validation, passer les erreurs au formulaire
        if (err.validationErrors && err.validationErrors.length > 0 && onError) {
          onError(err.validationErrors);
        } else {
          setSnackbar({
            open: true,
            message: err.message || 'Erreur lors de l\'enregistrement',
            severity: 'error'
          });
        }
        // Ne pas fermer le modal en cas d'erreur
      }
    } else {
      // Fermeture normale (annulation)
      setIsFormOpen(false);
      setSelectedItem(null);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({...snackbar, open: false});
  };
  
  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const visibleRows = React.useMemo(
    () =>
      [...salePointTypes]
        .filter((row) => 
          row.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, searchQuery, salePointTypes],
  );

  return (
    <Box sx={{ width: '100%', backgroundColor: '#f8f9fa' }}>
      <Paper 
        sx={{ 
          width: '100%', 
          mb: 2,
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
        }}
      >
        <EnhancedTableToolbar 
          searchQuery={searchQuery}
          onSearchChange={handleSearch}
          onAddClick={handleAddClick}
        />
        
        <TableContainer>
          <Table
            sx={{ 
              minWidth: 750,
              '& .MuiTableCell-root': {
                borderBottom: '1px solid #f0f0f0'
              }
            }}
            aria-labelledby="tableTitle"
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={salePointTypes.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = selected.includes(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, row.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      padding="normal"
                    >
                      {row.name}
                    </TableCell>
                    <TableCell align="right">
                      <ActionMenu 
                        row={row} 
                        onEditClick={handleEditClick} 
                        onShowClick={handleShowClick}
                        onDeleteClick={handleDeleteClick}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {visibleRows.length < rowsPerPage && (
                <TableRow
                  style={{
                    height: 53 * (rowsPerPage - visibleRows.length),
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={salePointTypes.filter(type => 
            type.name.toLowerCase().includes(searchQuery.toLowerCase())
          ).length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& .MuiTablePagination-toolbar": {
              backgroundColor: "#f5f5f5",
              display: 'flex',
              alignItems: 'center',
            },
            "& .MuiTablePagination-actions": {
              color: "blue",
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-input": {
              paddingRight: '16px',
            },
          }}
        />
      </Paper>

      <ItemForm
        open={isFormOpen}
        onClose={() => handleFormClose(null)}
        itemData={selectedItem}
        onSubmit={(formData, onError) => handleFormClose(formData, onError)}
        onCancel={() => handleFormClose(null)}
      />
      <Show
        open={isShowOpen}
        onClose={handleShowClose}
        item={selectedItem}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
